/**
 * Work lists — docs/11 §Setup → Work List.
 *
 * "Set up worklists and create customised to-do reminders for your team." Each
 * one is a standing question about the documents: what has been approved and
 * not acted on, what is late, what is waiting for somebody. They are derived,
 * never stored — a to-do that has to be ticked off by hand is a to-do that
 * goes stale.
 *
 * The catalogue is metadata; the query behind each is code, for the same
 * reason the reports are (docs/21 §8): a work list reads across documents, and
 * a configurable query language reading across documents is an injection
 * surface with a friendly name.
 */

export interface WorkListDef {
  readonly key: string
  readonly label: string
  /** The document type it is grouped under on the setup screen. */
  readonly groupedUnder: string
  readonly blurb: string
  /** What clears it, said plainly, because a list nobody can clear is noise. */
  readonly clearedBy: string
  /** Whether a row offers an action of its own, and what to call it. */
  readonly action?: { readonly key: string; readonly label: string }
  readonly enabledByDefault: boolean
  /** Only offered when the company has this setting on. */
  readonly requiresSetting?: string
}

const def = <const T extends WorkListDef>(w: T): T => w

export const WORK_LISTS = [
  def({
    key: 'purchase_plan_to_be_set',
    label: 'Purchase plan to be set for a material request',
    groupedUnder: 'material_request',
    blurb: 'Approved material requests waiting for a purchase plan. Procurement can begin only once one is set.',
    clearedBy: 'Setting the purchase plan on the request.',
    action: { key: 'setPurchasePlan', label: 'Plan set' },
    enabledByDefault: true,
    requiresSetting: 'purchase_plan_in_material_request',
  }),
  def({
    key: 'po_to_be_created',
    label: 'Purchase order to be created for a material request',
    groupedUnder: 'material_request',
    blurb: 'Approved material requests that nobody has raised a purchase order against yet.',
    clearedBy: 'Raising a purchase order for the request.',
    enabledByDefault: true,
  }),
  def({
    key: 'po_to_be_sent',
    label: 'Purchase order to be sent to the supplier',
    groupedUnder: 'purchase_order',
    blurb: 'Approved orders that have not been sent to the supplier yet.',
    clearedBy: 'Marking the order as sent, once it has gone out.',
    action: { key: 'markSent', label: 'Mark as sent' },
    enabledByDefault: true,
  }),
  def({
    key: 'overdue_deliveries',
    label: 'Deliveries overdue against a purchase order',
    groupedUnder: 'purchase_order',
    blurb: 'Approved orders whose delivery date has passed with nothing received against them.',
    clearedBy: 'Recording a receipt note, or cancelling what is not coming.',
    enabledByDefault: true,
  }),
  def({
    key: 'invoice_for_grn',
    label: "Supplier invoice to be recorded for a receipt note",
    groupedUnder: 'goods_receipt',
    blurb: 'Approved receipt notes with no supplier invoice recorded against them.',
    clearedBy: 'Recording the purchase invoice.',
    enabledByDefault: true,
  }),
  def({
    key: 'sco_to_be_sent',
    label: 'Subcontract order to be sent',
    groupedUnder: 'subcontract_order',
    blurb: 'Approved subcontract orders that have not been sent to the subcontractor.',
    clearedBy: 'Marking the order as sent.',
    action: { key: 'markSent', label: 'Mark as sent' },
    enabledByDefault: true,
  }),
  def({
    key: 'invoice_for_billing_request',
    label: 'Sales invoice to be raised for a billing request',
    groupedUnder: 'billing_request',
    blurb: 'Approved billing requests with no sales invoice raised against them.',
    clearedBy: 'Raising the sales invoice.',
    enabledByDefault: true,
  }),
  def({
    key: 'tools_still_out',
    label: 'Tools out past their return date',
    groupedUnder: 'tool_issue',
    blurb: 'Tools issued against a project whose expected return date has passed and which have not come back.',
    clearedBy: 'Recording the return, or extending the date on the issue.',
    enabledByDefault: true,
  }),
] as const satisfies readonly WorkListDef[]

export type WorkListKey = (typeof WORK_LISTS)[number]['key']

const byKey = new Map<string, WorkListDef>(WORK_LISTS.map((w) => [w.key as string, w as WorkListDef]))

export const getWorkListDef = (key: string): WorkListDef | undefined => byKey.get(key)

/** One row of a work list: a document, and why it is on the list. */
export interface WorkListRow {
  readonly documentId: string
  readonly docType: string
  readonly route: string
  readonly documentNo: string | null
  readonly documentDate: string
  readonly partyName: string | null
  readonly projectName: string | null
  readonly total: string
  /** Days since it landed here; the reason it is worth looking at. */
  readonly waitingDays: number
  readonly detail: string | null
}

export interface WorkListSummary {
  readonly key: string
  readonly label: string
  readonly blurb: string
  readonly clearedBy: string
  readonly groupedUnder: string
  readonly groupLabel: string
  readonly enabled: boolean
  readonly count: number
  readonly action: { readonly key: string; readonly label: string } | null
}
